import { PdfTemplate } from '../api/pdfTemplatesApi';

export function typesShareDefault(a: string, b: string): boolean {
  if (a === b) return true;
  const feeAliases = ['fee_notice', 'service_fee'];
  return feeAliases.includes(a) && feeAliases.includes(b);
}

const BUILDING_ASSET_PATH_RE = /\/buildings\/([0-9a-f-]{36})\//i;

function inferOperatorIdFromContent(content: unknown): string | null {
  if (!content || typeof content !== 'object') return null;
  const header = (content as Record<string, unknown>).header as
    | Record<string, unknown>
    | undefined;
  if (!header) return null;
  for (const key of ['logoPath', 'buildingLogoPath'] as const) {
    const p = header[key];
    if (typeof p === 'string' && p.length > 0) {
      const match = p.match(BUILDING_ASSET_PATH_RE);
      if (match?.[1]) return match[1];
    }
  }
  return null;
}

export function templateBelongsToOperator(
  template: PdfTemplate,
  operatorId: string,
): boolean {
  if (template.operatorId === operatorId) return true;
  if (template.operatorId) return false;
  return inferOperatorIdFromContent(template.content) === operatorId;
}

export function resolveTemplateOperatorId(
  template: PdfTemplate,
): string | null {
  return template.operatorId ?? inferOperatorIdFromContent(template.content);
}

export function getTemplateSourceLabel(params: {
  template: PdfTemplate;
  inherited: boolean;
  buildingSelected: boolean;
  scopeOperatorId: string | null | undefined;
  scopeOperatorName?: string | null;
  globalLabel: string;
}): string {
  const {
    template,
    inherited,
    buildingSelected,
    scopeOperatorId,
    scopeOperatorName,
    globalLabel,
  } = params;
  if (inherited) return 'Kế thừa chủ đầu tư';
  if (buildingSelected) return 'Riêng chung cư';
  if (!template.operatorId) {
    return globalLabel;
  }
  if (template.operator?.name) return `Chủ đầu tư: ${template.operator.name}`;
  if (scopeOperatorId === template.operatorId && scopeOperatorName) {
    return `Chủ đầu tư: ${scopeOperatorName}`;
  }
  return 'Chủ đầu tư';
}

function templateHasLogo(content: unknown): boolean {
  if (!content || typeof content !== 'object') return false;
  const header = (content as Record<string, unknown>).header as
    | Record<string, unknown>
    | undefined;
  if (!header) return false;
  return (
    (typeof header.logoPath === 'string' && header.logoPath.length > 0) ||
    (typeof header.buildingLogoPath === 'string' &&
      header.buildingLogoPath.length > 0)
  );
}

function pickForOperatorScope(
  group: PdfTemplate[],
  operatorId: string,
): PdfTemplate | undefined {
  const matches = group.filter((t) => templateBelongsToOperator(t, operatorId));
  if (matches.length === 0) return undefined;
  const explicit = matches.filter((t) => t.operatorId === operatorId);
  const scoped = explicit.length > 0 ? explicit : matches;
  const withLogo = scoped.filter((t) => templateHasLogo(t.content));
  const pool = withLogo.length > 0 ? withLogo : scoped;
  return pool[0];
}

function canonicalTemplateGroupKey(template: PdfTemplate): string {
  if (typesShareDefault(template.templateType, 'fee_notice')) {
    return 'fee_notice';
  }
  return `${template.templateType}:${template.name}`;
}

/** One default per logical type: operator scope wins over global. */
export function pickOperatorLevelDefaults(
  templates: PdfTemplate[],
  operatorId: string | null | undefined,
): PdfTemplate[] {
  const candidates = templates.filter(
    (t) => !t.buildingId && t.isDefault && t.isActive,
  );
  const byKey = new Map<string, PdfTemplate[]>();
  for (const t of candidates) {
    const key = canonicalTemplateGroupKey(t);
    const list = byKey.get(key) ?? [];
    list.push(t);
    byKey.set(key, list);
  }

  const picked: PdfTemplate[] = [];
  byKey.forEach((group) => {
    if (operatorId) {
      const operatorTpl = pickForOperatorScope(group, operatorId);
      if (operatorTpl) {
        picked.push(operatorTpl);
        return;
      }
    }
    const globalTpl = group.find((t) => !t.operatorId);
    if (globalTpl) {
      picked.push(globalTpl);
      return;
    }
    // Never show another operator's default when managing a specific operator scope.
    if (!operatorId) {
      picked.push(group[0]);
    }
  });

  return picked.sort((a, b) => a.name.localeCompare(b.name, 'vi'));
}

export type TemplateRow = {
  template: PdfTemplate;
  inherited: boolean;
};

export function buildTemplateRows(
  templates: PdfTemplate[],
  buildingId: string,
  operatorId: string | null | undefined,
  scopeOperatorId: string | null | undefined,
): TemplateRow[] {
  const buildingTemplates = templates.filter((t) => t.buildingId === buildingId);

  const operatorDefaults = buildingId
    ? pickOperatorLevelDefaults(templates, operatorId ?? null)
    : scopeOperatorId
      ? pickOperatorLevelDefaults(templates, scopeOperatorId)
      : templates.filter(
          (t) => !t.buildingId && t.isDefault && t.isActive && !t.operatorId,
        );

  if (!buildingId) {
    return operatorDefaults.map((t) => ({ template: t, inherited: false }));
  }

  const rows: TemplateRow[] = [];
  for (const op of operatorDefaults) {
    const override = buildingTemplates.find(
      (bt) => bt.isDefault && typesShareDefault(bt.templateType, op.templateType),
    );
    if (override) {
      rows.push({ template: override, inherited: false });
    } else {
      rows.push({ template: op, inherited: true });
    }
  }
  for (const bt of buildingTemplates) {
    if (bt.isDefault && !rows.some((r) => r.template.id === bt.id)) {
      rows.push({ template: bt, inherited: false });
    }
  }
  return rows;
}
